import { useEffect } from 'react'
import { Box, Button, Divider, Grid, IconButton, Stack, Typography } from '@mui/material'
import { useFieldArray } from 'react-hook-form'
import { Add, Delete, Edit } from '@mui/icons-material'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { v4 as uuidv4 } from 'uuid'
import Checkbox from 'components/generic/form/Checkbox'
import { TransparentIconLabelButton } from 'components/generic/buttons/IconLabelButton'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import { EMPLOYMENT_STATUS } from 'utils/constants.tsx'

const ContactsForm = ({ trigger, control, errors, watch, getValues, setValue, onShowComplianceInfo }) => {
  const { fields, append, remove } = useFieldArray({ control, name: 'contacts' })
  const watchFieldArray = watch('contacts')
  const controlledFields = fields.map((field, index) => ({ ...field, ...watchFieldArray[index] }))
  const haveComplianceInfo = index => !!getValues(`contacts.${index}.firstNameLegal`)
  const showRemoveButton = index => {
    const totalItems = controlledFields.length
    return !(index === 0 && totalItems === 1)
  }

  const getLocationsArray = () => {
    const locations = getValues('locations') || []
    const array = locations?.map(location => ({ id: location?.id, name: location?.name }))
    return [{ id: '', name: 'None' }, ...array]
  }

  const validatePrimaryContact = contactIndex => {
    const { locationId, isPrimaryContact } = controlledFields[contactIndex]
    if ((!locationId || locationId === '') && isPrimaryContact !== null) {
      setValue(`contacts.${contactIndex}.isPrimaryContact`, null)
    }
    return !locationId
  }

  useEffect(() => {
    trigger('contacts') // perform form validation on load
  }, [trigger])

  return (
    <Stack flexGrow={1} sx={{ pl: 3, pr: 4 }}>
      <Typography variant="h6" color="textSecondary">
        Contacts
      </Typography>
      <Box py={1}>
        <Divider />
      </Box>
      {controlledFields.map((field, index) => (
        <Stack key={`${field.id}`}>
          <Stack pt={3} direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <Typography variant="subtitle1" color="textSecondary">
              Contact {index + 1}
            </Typography>
            {showRemoveButton(index) && (
              <IconButton color="error" onClick={() => remove(index)}>
                <Delete />
              </IconButton>
            )}
          </Stack>
          <Grid container mt={1} spacing={2}>
            <Grid item md={6} xs={12}>
              <Input
                label="First Name"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.firstName}
                required
                helperText={errors.contacts?.[index]?.firstName?.message}
                name={`contacts.${index}.firstName`}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Input
                label="Last Name"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.lastName}
                required
                helperText={errors.contacts?.[index]?.lastName?.message}
                name={`contacts.${index}.lastName`}
              />
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2}>
            <Grid item md={6} xs={12}>
              <Input
                label="Email"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.email}
                helperText={errors.contacts?.[index]?.email?.message}
                name={`contacts.${index}.email`}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Input
                label="Role"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.role}
                helperText={errors.contacts?.[index]?.role?.message}
                name={`contacts.${index}.role`}
              />
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2}>
            <Grid item md={6} xs={12}>
              <Input
                label="Primary Phone"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.phone1}
                helperText={errors.contacts?.[index]?.phone1?.message}
                name={`contacts.${index}.phone1`}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Input
                label="Secondary Phone"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.phone2}
                helperText={errors.contacts?.[index]?.phone2?.message}
                name={`contacts.${index}.phone2`}
              />
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2}>
            <Grid item md={6} xs={12}>
              <Select
                name={`contacts.${index}.employmentStatus`}
                label="Employment Status"
                defaultValue=""
                control={control}
                items={[
                  { id: EMPLOYMENT_STATUS.EMPLOYED, name: 'Employed' },
                  { id: EMPLOYMENT_STATUS.SELF_EMPLOYED, name: 'Self Employed' },
                  { id: EMPLOYMENT_STATUS.RETIRED, name: 'Retired' },
                ]}
                error={!!errors.contacts?.[index]?.employmentStatus}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Input
                label="Place Of Employment"
                defaultValue=""
                control={control}
                error={!!errors.contacts?.[index]?.placeOfEmployment}
                helperText={errors.contacts?.[index]?.placeOfEmployment?.message}
                name={`contacts.${index}.placeOfEmployment`}
              />
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2} alignItems="center">
            <Grid item md={9} xs={7}>
              <Select
                label="Location"
                defaultValue=""
                control={control}
                items={getLocationsArray()}
                name={`contacts.${index}.locationId`}
                onClick={() => setValue(`contacts.${index}.isPrimaryContact`, null)}
              />
            </Grid>
            <Grid item md={3} xs={5} sx={{ textAlign: 'end' }}>
              <Checkbox
                label="Primary Contact"
                control={control}
                name={`contacts.${index}.isPrimaryContact`}
                disabled={validatePrimaryContact(index)}
              />
            </Grid>
          </Grid>
          <Grid container mt={1} spacing={2} alignItems="center">
            <Grid item md={8} xs={6}>
              <Input label="Notes" defaultValue="" control={control} name={`contacts.${index}.notes`} />
            </Grid>
            <Grid item md={4} xs={6}>
              <TransparentIconLabelButton
                startIcon={haveComplianceInfo(index) ? <Edit /> : <Add />}
                text={haveComplianceInfo(index) ? 'Edit Compliance Info' : 'Add Compliance Info'}
                onClick={() => onShowComplianceInfo(index)}
                sx={{ width: '100%' }}
              />
            </Grid>
          </Grid>
        </Stack>
      ))}
      <Box mt={1}>
        <Button startIcon={<Add />} onClick={() => append({ id: uuidv4() })}>
          Add New Contact
        </Button>
      </Box>
    </Stack>
  )
}

ContactsForm.propTypes = {
  trigger: PropTypes.func,
  control: PropTypes.object,
  errors: PropTypes.object,
  watch: PropTypes.func,
  getValues: PropTypes.func,
  setValue: PropTypes.func,
  onShowComplianceInfo: PropTypes.func,
}

ContactsForm.defaultProps = {
  trigger: noop,
  control: {},
  errors: {},
  watch: noop,
  getValues: noop,
  setValue: noop,
  onShowComplianceInfo: noop,
}

export default ContactsForm
